import React, { useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { updatedSelectedFilters } from '@actions/calendar-actions';
import FilterItem from '@components/calendar/filter-item';
import { getCalendarEventTypeKey } from '@utils/calendar-utils';
import { getCalendarEventItemsForType } from '@selectors/calendar-selector';
import { getCalendarEntity, getFiltersCalendar } from '@constants/config';
import './filter-list.scss';

// A base class for dialogs that shows a list of checkboxes.
const FilterList = ({ allText }) => {
  const dispatch = useDispatch();
  const dataType = getCalendarEventTypeKey();
  const calendarEntityType = getCalendarEntity();
  const baseItems = useSelector(state => getCalendarEventItemsForType(state, dataType));
  let items = baseItems;
  if (!R.isEmpty(getFiltersCalendar())) {
    // some cities want to limit the filters that show up on the calendar
    // so check the backend config and if it exists, filter to only those categories
    const filtersConfig = getFiltersCalendar()[calendarEntityType][dataType];
    items = R.pickBy((item, key) => (filtersConfig.indexOf(item.name) > -1 || key === dataType), baseItems);
  }
  const { filters } = useSelector(state => state.calendar);
  const selected = filters[dataType] || [];

  useEffect(() => {
    dispatch(updatedSelectedFilters(dataType, Object.keys(items)));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Returns true if all items are selected
  const isAllSelected = () => {
    // Comparison function
    if (selected) {
      const diff = (a, b) => a - b;  // eslint-disable-line id-length
      const sort = R.sort(diff);  // items might be unordered during selection
      return R.equals(sort(selected), sort(Object.keys(items)));
    }
    return false;
  };

  const onAllCheck = () => {
    if (isAllSelected()) {
      dispatch(updatedSelectedFilters(dataType, []));
    } else {
      dispatch(updatedSelectedFilters(dataType, Object.keys(items)));
    }
  };

  const onCheck = id => {
    if (selected && R.includes(id, selected)) {
      dispatch(updatedSelectedFilters(dataType, R.without([id], selected)));
    } else {
      dispatch(updatedSelectedFilters(dataType, R.uniq(R.append(id, selected || []))));
    }
  };

  return (
    <div styleName="filter-list">
      <FilterItem
        item={{name: allText}}
        checked={isAllSelected()}
        onCheck={onAllCheck}
      />
      {Object.keys(items).map(key => (
        <FilterItem
          key={key}
          id={key}
          item={items[key]}
          checked={R.includes(key, selected)}
          onCheck={onCheck}
        />
      ))}
    </div>
  );
};

FilterList.propTypes = {
  allText: PropTypes.string
};

export default memo(FilterList);
